import { useState, useEffect, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import AdminDashboard from "@/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [loads, setLoads] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    practices_used: 0,
    practice_limit: 2,
  });
  const [filters, setFilters] = useState({
    origin: "",
    destination: "",
    equipment: "",
    minPay: "",
  });
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showManagerLogin, setShowManagerLogin] = useState(false);
  const [loginCode, setLoginCode] = useState("");
  const [userRole, setUserRole] = useState(null); // null, 'master', or 'manager'
  const [userData, setUserData] = useState(null);

  // Fetch loads
  useEffect(() => {
    fetchLoads();
    fetchSubscriptionStatus();
    checkPaymentReturn();
  }, []);

  const checkPaymentReturn = async () => {
    // Check if returning from Stripe payment
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const subscriptionSuccess = urlParams.get('subscription_success');
    
    if (sessionId && subscriptionSuccess) {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      alert("Payment verification timeout. Please refresh the page or contact support.");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/subscribe/check-status/${sessionId}`);
      
      if (response.data.status === "paid") {
        alert("✓ Subscription activated successfully!\n\nYou now have unlimited access to book loads.");
        fetchSubscriptionStatus();
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === "expired") {
        alert("Payment session expired. Please try again.");
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts === 0) {
        alert("Error verifying payment. Please contact support if you were charged.");
      }
    }
  };

  const fetchLoads = async () => {
    try {
      const response = await axios.get(`${API}/loads`);
      setLoads(response.data);
    } catch (error) {
      console.error("Error fetching loads:", error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription-status`);
      setSubscriptionStatus(response.data);
      setIsSubscribed(response.data.is_subscribed);
    } catch (error) {
      console.error("Error fetching subscription status:", error);
    }
  };

  const handleSubscriptionToggle = async (e) => {
    const newStatus = e.target.checked;
    
    if (newStatus && !isSubscribed) {
      // User wants to subscribe - redirect to payment
      if (window.confirm("Subscribe to Urbanvision Load Board for $49.99/month?\n\n✓ Unlimited real bookings\n✓ No practice limits\n✓ Priority access to loads")) {
        try {
          const response = await axios.post(
            `${API}/subscribe/create-checkout`,
            {},
            {
              params: { carrier_id: "demo_carrier" },
              headers: { origin: window.location.origin }
            }
          );
          
          if (response.data.url) {
            // Redirect to Stripe checkout
            window.location.href = response.data.url;
          }
        } catch (error) {
          alert("Error creating checkout session: " + error.message);
          console.error("Checkout error:", error);
        }
      }
    } else {
      // Toggle for demo purposes (non-payment)
      setIsSubscribed(newStatus);
      try {
        await axios.post(`${API}/subscription-status?carrier_id=demo_carrier&is_subscribed=${newStatus}`);
        fetchSubscriptionStatus();
      } catch (error) {
        console.error("Error updating subscription:", error);
      }
    }
  };

  const handleBooking = async (load) => {
    if (isSubscribed) {
      alert(`This is where you'd call your booking API.\n\nExample:\nPOST /api/loads/${load.id}/book`);
      return;
    }

    if (subscriptionStatus.practices_used >= subscriptionStatus.practice_limit) {
      alert("Preview limit reached.\n\nYou can see loads, but you must subscribe to actually book.");
      return;
    }

    try {
      await axios.post(`${API}/bookings`, {
        load_id: load.id,
        carrier_id: "demo_carrier",
        is_practice: true,
      });
      
      const remaining = subscriptionStatus.practice_limit - subscriptionStatus.practices_used - 1;
      alert(`Practice booking recorded for Load #${load.id}.\n\nPreview practice loads remaining: ${remaining}`);
      
      fetchSubscriptionStatus();
    } catch (error) {
      alert(error.response?.data?.detail || "Error booking load");
      console.error("Error booking load:", error);
    }
  };

  const scrollToLoadBoard = () => {
    document.getElementById("loadboard")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, {
        master_code: loginCode,
      });
      if (response.data.success) {
        setUserRole('master');
        setUserData({
          code: response.data.code,
          role: 'master',
          name: 'Master Admin'
        });
        setShowAdminLogin(false);
        setLoginCode("");
      }
    } catch (error) {
      alert("Invalid master code!");
      console.error("Admin login error:", error);
    }
  };

  const handleManagerLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/manager/login`, {
        code: loginCode,
      });
      if (response.data.success) {
        setUserRole('manager');
        setUserData({
          code: response.data.code,
          role: 'manager',
          name: response.data.name,
          email: response.data.email
        });
        setShowManagerLogin(false);
        setLoginCode("");
      }
    } catch (error) {
      alert("Invalid manager code!");
      console.error("Manager login error:", error);
    }
  };

  const handleLogout = () => {
    setUserRole(null);
    setUserData(null);
    setLoginCode("");
  };

  const filteredLoads = useMemo(() => {
    return loads.filter((load) => {
      const o = filters.origin.trim().toLowerCase();
      const d = filters.destination.trim().toLowerCase();
      const e = filters.equipment;
      const minPay = filters.minPay ? parseFloat(filters.minPay) : 0;

      if (o && !load.origin.toLowerCase().includes(o)) return false;
      if (d && !load.destination.toLowerCase().includes(d)) return false;
      if (e && load.equipment !== e) return false;
      if (minPay && load.pay < minPay) return false;
      return true;
    });
  }, [loads, filters]);

  const heroLoads = loads.slice(0, 3);

  // If admin or manager is logged in, show dashboard
  if (userRole) {
    return <AdminDashboard userData={userData} onLogout={handleLogout} />;
  }

  return (
    <div className="App">
      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div className="modal-overlay" onClick={() => setShowAdminLogin(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Admin Login (Master Code)</h2>
            <form onSubmit={handleAdminLogin}>
              <input
                type="password"
                placeholder="Enter Master Code (4 digits)"
                value={loginCode}
                onChange={(e) => setLoginCode(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  borderRadius: "0.5rem",
                  border: "1px solid var(--border-subtle)",
                  background: "#10101a",
                  color: "var(--text-main)",
                  marginBottom: "1rem",
                }}
              />
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button type="submit" className="btn-primary">
                  Login
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setShowAdminLogin(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manager Login Modal */}
      {showManagerLogin && (
        <div className="modal-overlay" onClick={() => setShowManagerLogin(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Manager Login</h2>
            <form onSubmit={handleManagerLogin}>
              <input
                type="password"
                placeholder="Enter Manager Code (4 digits)"
                value={loginCode}
                onChange={(e) => setLoginCode(e.target.value)}
                autoFocus
                maxLength="4"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  borderRadius: "0.5rem",
                  border: "1px solid var(--border-subtle)",
                  background: "#10101a",
                  color: "var(--text-main)",
                  marginBottom: "1rem",
                }}
              />
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button type="submit" className="btn-primary">
                  Login
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setShowManagerLogin(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <header>
        <div className="nav">
          <div className="brand">
            <div className="brand-title">URBANVISION LOGISTICS</div>
            <div className="brand-sub">Verified Capacity • Box Trucks • Sprinters • Cargo Vans</div>
          </div>
          <nav className="nav-links">
            <a href="#services">Services</a>
            <a href="#shippers">Shippers</a>
            <a href="#brokers">Brokers</a>
            <a href="#carriers">Carriers</a>
            <a href="#training">Training</a>
            <a href="#pricing">Pricing</a>
            <button className="btn-ghost" onClick={() => setShowManagerLogin(true)}>
              Manager
            </button>
            <button className="btn-ghost" onClick={() => setShowAdminLogin(true)}>
              Admin
            </button>
            <button className="nav-cta" onClick={scrollToLoadBoard}>View Load Board</button>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section>
          <div className="hero">
            <div>
              <h1 className="hero-title">
                Reliable <span className="hero-highlight">box truck & cargo van</span> capacity on demand.
              </h1>
              <p className="hero-sub">
                Urbanvision Logistics connects shippers and brokers with verified MC/DOT carriers, real-time tracking,
                and specialty capacity for final-mile and middle-mile freight.
              </p>
              <div className="hero-pills">
                <span className="pill">Verified MC/DOT Only</span>
                <span className="pill">Real-Time GPS Tracking</span>
                <span className="pill">Sprinters • Cargo Vans • Box Trucks</span>
              </div>
              <div className="hero-actions">
                <button className="btn-primary" onClick={scrollToLoadBoard}>Preview Load Board</button>
                <button className="btn-primary" onClick={() => {
                  if (window.confirm("Subscribe to Urbanvision Load Board for $49.99/month?\n\n✓ Unlimited real bookings\n✓ No practice limits\n✓ Priority access to loads")) {
                    axios.post(`${API}/subscribe/create-checkout`, {}, {
                      params: { carrier_id: "demo_carrier" },
                      headers: { origin: window.location.origin }
                    }).then(response => {
                      if (response.data.url) window.location.href = response.data.url;
                    }).catch(error => alert("Error: " + error.message));
                  }
                }}>
                  Subscribe Now - $49.99/mo
                </button>
                <button className="btn-ghost" onClick={() => alert('Hook this to your carrier onboarding flow.')}>
                  Become a Verified Carrier
                </button>
              </div>
              <div className="hero-meta">
                2-day preview • 2 practice loads per week • No booking until you subscribe to the Urbanvision Load Board.
              </div>
            </div>
            <aside className="hero-panel">
              <div className="hero-panel-badge">
                <span>●</span> Live capacity snapshot (demo)
              </div>
              <div className="hero-panel-title">Today's Sample Loads</div>
              <ul className="hero-panel-list">
                {heroLoads.map((load, index) => (
                  <li key={index} style={{ display: "flex", gap: "0.5rem" }}>
                    <div className="hero-dot"></div>
                    <div>
                      <strong>{load.origin}</strong> → <strong>{load.destination}</strong><br/>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                        {load.equipment} • ${load.pay} • {load.pickup}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="hero-meta">✅ Connected to MongoDB • Real-time load data • FastAPI Backend Active</div>
            </aside>
          </div>
        </section>

        {/* Services Section */}
        <section id="services">
          <h2 className="section-title">Who We Serve</h2>
          <p className="section-sub">
            Urbanvision is built for shippers, brokers, and carriers who are tired of broken promises, ghost trucks,
            and garbage freight. This is a capacity network you can actually trust.
          </p>
          <div className="grid-3">
            <div className="card" id="shippers">
              <div className="card-tag">For shippers</div>
              <h3>Guaranteed backup capacity</h3>
              <p>
                Use Urbanvision as your backup or primary carrier network. Verified MC/DOT carriers, time-sensitive
                deliveries, and live tracking on every load.
              </p>
            </div>
            <div className="card" id="brokers">
              <div className="card-tag">For brokers</div>
              <h3>Rescue lanes & ugly freight coverage</h3>
              <p>
                Post your tough lanes for free. Urbanvision carriers specialize in final-mile, odd lanes, and last-minute
                fall-off rescue.
              </p>
            </div>
            <div className="card" id="carriers">
              <div className="card-tag">For carriers</div>
              <h3>A board that respects your time</h3>
              <p>
                Real freight, verified partners, and home-radius matching. See loads in preview mode, then subscribe to
                unlock booking and priority access.
              </p>
            </div>
          </div>
        </section>

        {/* Training Section */}
        <section id="training">
          <h2 className="section-title">Urbanvision Academy</h2>
          <p className="section-sub">
            Every shipper, broker, carrier, and dispatcher plugged into Urbanvision has access to structured training:
            sales, operations, compliance, and customer experience.
          </p>
          <div className="grid-3">
            <div className="card">
              <h3>Shipper & broker sales playbooks</h3>
              <p>Full talking tracks, objection handling, and test-load strategies for growing your book of business.</p>
            </div>
            <div className="card">
              <h3>Carrier & dispatch operations manuals</h3>
              <p>Step-by-step driver communication, tracking, POD standards, and exception handling procedures.</p>
            </div>
            <div className="card">
              <h3>FMCSA-aligned compliance structure</h3>
              <p>Built to align with FMCSA guidelines, MC/DOT verification, and safety-first operational standards.</p>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing">
          <h2 className="section-title">Pricing Overview</h2>
          <p className="section-sub">
            Start in preview mode. When you're ready to move real freight consistently, upgrade to a subscription.
          </p>
          <div className="grid-3">
            <div className="card">
              <div className="card-tag">Carriers</div>
              <h3>Load Board Subscription</h3>
              <p>
                Unlock booking rights, unlimited lanes, and priority freight.
              </p>
              <p><strong>$49/month</strong> (example – adjust as needed)</p>
            </div>
            <div className="card">
              <div className="card-tag">Brokers</div>
              <h3>Broker Access</h3>
              <p>Free posting, trained carrier network, and fall-off rescue. Pay only when freight moves.</p>
            </div>
            <div className="card">
              <div className="card-tag">Shippers</div>
              <h3>Shipper Portal</h3>
              <p>Portal access, full tracking visibility, and dedicated account management.</p>
            </div>
          </div>
        </section>

        {/* Load Board Section */}
        <section id="loadboard">
          <div className="loadboard">
            <div className="lb-header">
              <div>
                <div className="lb-title">Urbanvision Load Board (Demo Front-End)</div>
                <div className="lb-meta">
                  View sample loads below. In preview mode, carriers see loads and can practice, but cannot book until they subscribe.
                </div>
              </div>
              <div>
                <div className={`lb-mode-badge ${!isSubscribed ? 'preview' : ''}`}>
                  {isSubscribed 
                    ? "Subscribed Mode • Booking Enabled"
                    : "Preview Mode • 2 practice loads / week • No booking"
                  }
                </div>
                <div className="lb-controls">
                  <label className="lb-toggle">
                    <input 
                      type="checkbox" 
                      id="subscriptionToggle" 
                      checked={isSubscribed}
                      onChange={handleSubscriptionToggle}
                    />
                    <span>Simulate subscribed carrier</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="lb-filters">
              <div>
                <label htmlFor="filterOrigin">Origin</label>
                <input 
                  id="filterOrigin" 
                  placeholder="e.g. Atlanta, GA" 
                  value={filters.origin}
                  onChange={(e) => setFilters({ ...filters, origin: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="filterDestination">Destination</label>
                <input 
                  id="filterDestination" 
                  placeholder="e.g. Charlotte, NC" 
                  value={filters.destination}
                  onChange={(e) => setFilters({ ...filters, destination: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="filterEquipment">Equipment</label>
                <select 
                  id="filterEquipment" 
                  value={filters.equipment}
                  onChange={(e) => setFilters({ ...filters, equipment: e.target.value })}
                >
                  <option value="">Any</option>
                  <option value="Cargo Van">Cargo Van</option>
                  <option value="Sprinter Van">Sprinter Van</option>
                  <option value="Box Truck 26'">Box Truck 26'</option>
                  <option value="Box Truck 16'">Box Truck 16'</option>
                </select>
              </div>
              <div>
                <label htmlFor="filterMinPay">Min Pay ($)</label>
                <input 
                  id="filterMinPay" 
                  type="number" 
                  min="0" 
                  placeholder="e.g. 300" 
                  value={filters.minPay}
                  onChange={(e) => setFilters({ ...filters, minPay: e.target.value })}
                />
              </div>
            </div>

            <div className="lb-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Origin → Destination</th>
                    <th>Pickup</th>
                    <th>Equipment</th>
                    <th>Weight</th>
                    <th>Pay</th>
                    <th>Tags</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLoads.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center", padding: "0.8rem", color: "var(--text-muted)" }}>
                        No loads match your filters. Adjust and try again.
                      </td>
                    </tr>
                  ) : (
                    filteredLoads.map((load) => (
                      <tr key={load.id}>
                        <td>
                          <strong>{load.origin}</strong> → <strong>{load.destination}</strong><br/>
                          <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>ID: #{load.id.substring(0, 8)}</span>
                        </td>
                        <td>{load.pickup}</td>
                        <td>
                          <span className="lb-badge lb-badge-equip">{load.equipment}</span>
                        </td>
                        <td>{load.weight}</td>
                        <td>
                          <span className="lb-pay">${load.pay}</span>
                        </td>
                        <td>
                          {load.tags.map((tag, idx) => (
                            <span key={idx} className="lb-badge lb-badge-tag">{tag}</span>
                          ))}
                        </td>
                        <td>
                          <button
                            className={`lb-book-btn ${
                              !isSubscribed && subscriptionStatus.practices_used >= subscriptionStatus.practice_limit
                                ? "disabled"
                                : ""
                            }`}
                            onClick={() => handleBooking(load)}
                            disabled={!isSubscribed && subscriptionStatus.practices_used >= subscriptionStatus.practice_limit}
                          >
                            {isSubscribed 
                              ? "Book Load" 
                              : !isSubscribed && subscriptionStatus.practices_used >= subscriptionStatus.practice_limit
                              ? "Subscribe to Book"
                              : "Practice Book"
                            }
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="lb-footer-note">
                {isSubscribed ? (
                  <>
                    Subscribed Mode: <span>Live booking enabled</span>. Connect this UI to your production API.
                  </>
                ) : (
                  <>
                    Preview Mode: <span>
                      {subscriptionStatus.practice_limit - subscriptionStatus.practices_used} practice “book” clicks remaining
                    </span>. Real booking requires an active Urbanvision carrier subscription.
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div><strong>Urbanvision Logistics</strong> • Box Truck & Cargo Van Capacity Network</div>
        <div>✅ FMCSA-aligned practices • ✅ MC/DOT-verified carriers • ✅ Real-time tracking on every load</div>
        <div style={{ marginTop: "0.4rem" }}>
          <strong style={{ color: "var(--accent-gold-soft)" }}>Backend Connected:</strong> FastAPI + MongoDB | 
          <strong style={{ color: "var(--accent-gold-soft)" }}> Payments:</strong> Stripe Integration Active | 
          <strong style={{ color: "var(--accent-gold-soft)" }}> API:</strong> Broker Integration Ready
        </div>
      </footer>
    </div>
  );
}

export default App;
